import {
    FieldValues,
    UseFieldArrayProps,
    UseFieldArrayReturn,
    useFieldArray,
    FieldArrayPath,
} from 'react-hook-form';
import { useLocallyRemovedEventFormFieldsStore } from '../event-panel/event-panel-form/form-persistence/use-locally-removed-fields';
import { useCallback } from 'react';

export type EventFormFieldArrayBaseKey =
    | 'vehicles'
    | 'mergedVehicles'
    | 'towVehicles'
    | 'mergedTowVehicles'
    | 'firearms'
    | 'mergedFirearms'
    | 'drugs'
    | 'mergedDrugs'
    | 'otherItems'
    | 'mergedOtherItems';

export type UseTrackedFieldArrayRemove = (
    fieldId: number | undefined | null,
    index: number
) => void;

/**
 * Locally tracks fields removed through this hook. See integrated-event-panel-form.tsx for the main use case.
 */
export function useTrackedFieldArray<
    TFieldValues extends FieldValues = FieldValues,
    TFieldArrayName extends FieldArrayPath<TFieldValues> = FieldArrayPath<TFieldValues>,
    TKeyName extends string = 'id'
>(
    props: UseFieldArrayProps<TFieldValues, TFieldArrayName, TKeyName>
): Omit<UseFieldArrayReturn<TFieldValues, TFieldArrayName, TKeyName>, 'remove'> & {
    remove: UseTrackedFieldArrayRemove;
} {
    const fieldArray = useFieldArray(props);

    const store = useLocallyRemovedEventFormFieldsStore();

    const remove = useCallback(
        (fieldId: number | undefined | null, index: number) => {
            fieldArray.remove(index);

            if (fieldId !== undefined && fieldId !== null) {
                store.removeField(props.name as EventFormFieldArrayBaseKey, fieldId);
            }
        },
        [fieldArray, props.name, store]
    );

    return { ...fieldArray, remove };
}
